import { useEffect, useState } from "react";

/**
 * Returns true when animations should be stopped, combining:
 *   - The OS-level `prefers-reduced-motion: reduce` media query.
 *   - The accessibility widget's `a11y-stop-animations` class on `<html>`.
 *
 * Updates live when either source changes.
 */
export function useStopAnimations(): boolean {
  const [stop, setStop] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const widget = document.documentElement.classList.contains("a11y-stop-animations");
    return reduced || widget;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");

    const compute = () =>
      mq.matches || document.documentElement.classList.contains("a11y-stop-animations");

    const update = () => setStop(compute());

    // OS preference change
    mq.addEventListener?.("change", update);

    // Accessibility widget toggling the class on <html>
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    // Sync once in case anything changed between initial state and effect
    update();

    return () => {
      mq.removeEventListener?.("change", update);
      observer.disconnect();
    };
  }, []);

  return stop;
}
